import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { PropsWithChildren, useCallback } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'
import { TextVariant } from '../Text'

type Props = {
  component?: React.ElementType
  textVariant?: TextVariant
  icon?: string
  iconPlacement?: 'start' | 'end'
  iconProps?: { [key: string]: any }
  src?: string
  className?: string
  readonly?: boolean
  onClick?: () => void
} & React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>

export default function MenuItem({
  component = 'li',
  textVariant = 'body2',
  icon = undefined,
  iconPlacement = 'start',
  iconProps = {},
  src = undefined,
  onClick,
  className,
  readonly = false,
  children,
  ...props
}: PropsWithChildren<Props>) {
  const classes = useStyles()
  const handleClick = useCallback(
    (e) => {
      if (readonly) {
        e.stopPropagation()
        return
      }
      onClick(e)
    },
    [onClick, readonly],
  )
  const content = (
    <Text
      data-testid={generateTestId(children[1])}
      variant={textVariant}
      component={component}
      className={clsx(classes.menuItem, className, {
        [classes.menuItemIconEnd]: iconPlacement === 'end',
        [classes.menuItemReadonly]: readonly,
      })}
      onClick={!src ? handleClick : undefined}
      {...(props as any)}
    >
      {icon && (
        <FontAwesomeIcon
          size="md"
          className={clsx(classes.icon, { [classes.iconReadonly]: readonly })}
          {...iconProps}
        >
          {icon}
        </FontAwesomeIcon>
      )}
      {children}
    </Text>
  )
  if (src) {
    return (
      <SimpleLink src={src} onClick={onClick} className={classes.link}>
        {content}
      </SimpleLink>
    )
  }
  return content
}

const useStyles = makeStyles<Theme>((theme) => ({
  menuItem: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    justifyContent: 'start',
    gap: 16,
    minHeight: 32,
    alignItems: 'center',
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: theme.components.checkbox.background,
    transition: 'background .2s ease, color .2s ease',
    color: theme.components.checkbox.color,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.components.checkbox.hoverBackground,
    },

    '& i': {
      transition: 'color .2s ease',
      marginTop: 1,
      fontSize: 18,
      width: 20,
      height: 20,
      display: 'grid',
      alignItems: 'center',
      justifyContent: 'center',
      order: 0,
    },
  },
  menuItemIconEnd: {
    justifyContent: 'space-between',
    '& i': {
      fontSize: 14,
      order: 1,
    },
  },
  menuItemReadonly: {
    color: theme.components.checkbox.disabledColor,
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  link: {
    textDecoration: 'none !important',
    display: 'grid',
  },
  icon: {
    color: theme.components.checkbox.color,
  },
  iconReadonly: {
    color: theme.components.checkbox.disabledColor,
  },
}))
